import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, ApiError, type ScheduledMessageLogView } from '../lib/api'
import {
  describeRecurrence,
  RecurrenceFields,
  recurrencePayload,
  recurrenceToState,
  type RecurrenceState,
} from './RecurrenceFields'

/** Local-datetime-input string ("2026-08-14T09:30") from an ISO timestamp, for the edit form's default value. */
function toLocalInput(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const STATUS_TONE: Record<string, 'good' | 'warn' | 'bad' | undefined> = {
  pending: undefined,
  sending: 'warn',
  sent: 'good',
  cancelled: undefined,
  failed: 'bad',
}

/**
 * Every scheduled message across every chat, in one place — the audit and cancel surface a
 * single chat's compose bar cannot be, since a message booked from another conversation is
 * invisible there. Same relationship `Broadcasts.tsx`'s history table has to composing a send.
 */
const STATUS_FILTERS = ['all', 'pending', 'sending', 'sent', 'cancelled', 'failed'] as const

export function ScheduledMessages() {
  const queryClient = useQueryClient()
  const data = useQuery({ queryKey: ['scheduled-messages-log'], queryFn: api.scheduledMessages, retry: false })
  const [editingId, setEditingId] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]>('all')
  const [selectMode, setSelectMode] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkOutcome, setBulkOutcome] = useState<string | null>(null)
  const cancel = useMutation({
    mutationFn: (id: string) => api.cancelScheduledMessage(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['scheduled-messages-log'] }),
  })
  const bulkCancel = useMutation({
    mutationFn: async () => {
      const ids = [...selected]
      const results = await Promise.all(
        ids.map((id) =>
          api
            .cancelScheduledMessage(id)
            .then(() => ({ id, ok: true as const }))
            .catch((err) => ({ id, ok: false as const, error: err instanceof ApiError ? err.message : 'Failed' })),
        ),
      )
      return results
    },
    onSuccess: (results) => {
      const failed = results.filter((r) => !r.ok)
      setBulkOutcome(
        failed.length === 0
          ? `Cancelled ${results.length} send${results.length === 1 ? '' : 's'}.`
          : `Cancelled ${results.length - failed.length} of ${results.length} — some did not go through.`,
      )
      setSelected(new Set())
      void queryClient.invalidateQueries({ queryKey: ['scheduled-messages-log'] })
    },
  })
  const toggleSelected = (id: string) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const save = useMutation({
    mutationFn: (vars: { id: string; body: string; scheduledFor: string; recurrenceState: RecurrenceState }) =>
      api.updateScheduledMessage(vars.id, {
        body: vars.body,
        scheduledFor: vars.scheduledFor,
        ...recurrencePayload(vars.recurrenceState),
      }),
    onSuccess: () => {
      setEditingId(null)
      void queryClient.invalidateQueries({ queryKey: ['scheduled-messages-log'] })
    },
  })

  if (data.isPending) return <div className="cad-card" style={{ height: '200px' }} />
  if (data.isError) {
    const message = data.error instanceof ApiError ? data.error.message : 'Unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const messages = data.data!.messages
  const pending = messages.filter((m) => m.status === 'pending').length
  const visibleMessages = statusFilter === 'all' ? messages : messages.filter((m) => m.status === statusFilter)

  return (
    <>
      <header className="stack gap-1">
        <span className="cad-eyebrow">Scheduling</span>
        <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
          <h1 className="cad-title">
            {pending} scheduled message{pending === 1 ? '' : 's'} coming up
          </h1>
          {messages.length > 0 && (
            <button
              type="button"
              className="cad-btn"
              data-variant={selectMode ? undefined : 'quiet'}
              onClick={() => {
                setSelectMode((v) => !v)
                setSelected(new Set())
                setBulkOutcome(null)
              }}
            >
              {selectMode ? 'Done selecting' : 'Select'}
            </button>
          )}
        </div>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          Every message written now and set to send later, across every chat — booked from a
          chat's own compose bar with the clock control. A recurring one shows here as whichever
          occurrence is next; cancelling it stops the series, not just that send.
        </p>
        {cancel.error && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {cancel.error instanceof ApiError || cancel.error instanceof Error
              ? cancel.error.message
              : 'Could not cancel that.'}
          </p>
        )}
      </header>

      <div className="row gap-2 wrap">
        {STATUS_FILTERS.map((value) => (
          <button
            key={value}
            type="button"
            className="cad-pill"
            data-tone={statusFilter === value ? 'accent' : undefined}
            onClick={() => setStatusFilter(value)}
          >
            {value === 'all' ? 'All' : value[0].toUpperCase() + value.slice(1)}
          </button>
        ))}
      </div>

      {selectMode && selected.size > 0 && (
        <div className="cad-card">
          <div className="stack gap-2">
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
              {selected.size} send{selected.size === 1 ? '' : 's'} selected
            </span>
            <div className="row gap-2 wrap">
              <button
                type="button"
                className="cad-btn"
                data-variant="text"
                disabled={bulkCancel.isPending}
                onClick={() => {
                  if (confirm(`Cancel ${selected.size} scheduled message${selected.size === 1 ? '' : 's'}?\n\nThey will not be sent, and the text written for each is discarded.`)) bulkCancel.mutate()
                }}
              >
                Cancel selected
              </button>
            </div>
            {bulkOutcome && <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>{bulkOutcome}</span>}
          </div>
        </div>
      )}

      <section className="cad-card">
        {visibleMessages.length === 0 ? (
          <p className="cad-muted">{messages.length === 0 ? 'Nothing has been scheduled yet.' : 'Nothing matches this filter.'}</p>
        ) : (
          <div className="cad-grid-frame">
            <table className="cad-table">
              <thead>
                <tr>
                  {selectMode && <th />}
                  <th>Chat</th>
                  <th>Message</th>
                  <th>When</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visibleMessages.map((m) =>
                  editingId === m.id ? (
                    <EditRow
                      key={m.id}
                      message={m}
                      colSpan={selectMode ? 6 : 5}
                      saving={save.isPending}
                      error={save.error instanceof ApiError || save.error instanceof Error ? save.error.message : undefined}
                      // `save` is one mutation shared by every row's editor — without resetting
                      // it here, a failed save on this message kept showing as an error on
                      // whichever other message got opened for editing next.
                      onCancel={() => {
                        save.reset()
                        setEditingId(null)
                      }}
                      onSave={(body, scheduledFor, recurrenceState) =>
                        save.mutate({ id: m.id, body, scheduledFor, recurrenceState })
                      }
                    />
                  ) : (
                    <tr key={m.id}>
                      {selectMode && (
                        <td>
                          {m.status === 'pending' && (
                            <input
                              type="checkbox"
                              aria-label={`Select the send to ${m.conversationSubject ?? 'this chat'}`}
                              checked={selected.has(m.id)}
                              onChange={() => toggleSelected(m.id)}
                            />
                          )}
                        </td>
                      )}
                      <td
                        className="cad-muted"
                        style={{ maxWidth: '32ch', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={m.conversationSubject ?? undefined}
                      >
                        {m.conversationSubject ?? '—'}
                      </td>
                      <td className="cad-muted" style={{ maxWidth: '32ch', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.body}
                      </td>
                      <td>
                        {new Date(m.scheduledFor).toLocaleString()}
                        {m.recurrence && (
                          <span className="cad-pill" style={{ marginLeft: '6px' }}>
                            repeats {describeRecurrence(m.recurrence, m.recurrenceInterval, m.recurrenceEndAt, m.recurrenceEndCount, m.recurrenceWeekdays)}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="cad-pill" data-tone={STATUS_TONE[m.status]}>
                          {m.status}
                        </span>
                        {m.status === 'failed' && m.error && (
                          <span className="cad-muted" style={{ marginLeft: '6px', fontSize: 'var(--cad-text-base)' }}>{m.error}</span>
                        )}
                      </td>
                      <td>
                        {m.status === 'pending' && (
                          <div className="row gap-2">
                            <button
                              type="button"
                              className="cad-btn"
                              data-variant="text"
                              onClick={() => {
                                save.reset()
                                setEditingId(m.id)
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="cad-btn"
                              data-variant="text"
                              disabled={cancel.isPending}
                              onClick={() => {
                                if (confirm(`Cancel this scheduled message?

It will not be sent, and the text you wrote is discarded.`)) cancel.mutate(m.id)
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}

/** Inline edit — body, time, and repeat settings for one still-pending occurrence. */
function EditRow({
  message,
  colSpan,
  saving,
  error,
  onSave,
  onCancel,
}: {
  message: ScheduledMessageLogView
  colSpan: number
  saving: boolean
  error?: string
  onSave: (body: string, scheduledFor: string, recurrenceState: RecurrenceState) => void
  onCancel: () => void
}) {
  const [body, setBody] = useState(message.body)
  const [scheduledFor, setScheduledFor] = useState(toLocalInput(message.scheduledFor))
  const [recurrenceState, setRecurrenceState] = useState<RecurrenceState>(
    recurrenceToState(
      message.recurrence,
      message.recurrenceInterval,
      message.recurrenceEndAt,
      message.recurrenceEndCount,
      message.recurrenceWeekdays,
    ),
  )

  return (
    <tr>
      <td colSpan={colSpan}>
        <div className="stack gap-2" style={{ padding: '8px 0' }}>
          <textarea
            className="cad-input"
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="row gap-2 wrap" style={{ alignItems: 'flex-end' }}>
            <div className="cad-field">
              <label htmlFor={`sched-edit-when-${message.id}`}>When</label>
              <input
                id={`sched-edit-when-${message.id}`}
                className="cad-input"
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
              />
            </div>
            <div className="cad-field">
              <label htmlFor={`sched-edit-repeat-${message.id}`}>Repeat</label>
              <select
                id={`sched-edit-repeat-${message.id}`}
                className="cad-input"
                value={recurrenceState.recurrence}
                onChange={(e) =>
                  setRecurrenceState({ ...recurrenceState, recurrence: e.target.value as RecurrenceState['recurrence'] })
                }
              >
                <option value="">Once</option>
                <option value="daily">Repeats daily</option>
                <option value="weekly">Repeats weekly</option>
                <option value="monthly">Repeats monthly</option>
              </select>
            </div>
          </div>
          <RecurrenceFields state={recurrenceState} onChange={setRecurrenceState} idPrefix={`sched-edit-${message.id}`} />
          {error && (
            <span className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
              {error}
            </span>
          )}
          <div className="row gap-2">
            <button
              type="button"
              className="cad-btn"
              disabled={saving || !body.trim() || !scheduledFor}
              onClick={() => onSave(body.trim(), new Date(scheduledFor).toISOString(), recurrenceState)}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="cad-btn" data-variant="text" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </div>
      </td>
    </tr>
  )
}
